import { EntityCommandInformation } from "./EntityCommandInformation";
import { PartialNullable } from "./Extensions";
import { NAMESPACE } from "./_Namespace";
import { PropertyChange } from "./PropertyChange";
import { IRadObject, Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString, RadObject, RadObjectPropertyValues } from "./RadObject";
import { IEvaluator } from "./Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { ControlConstructor } from "./Ui/Controls/Control";

export enum EntityCommandTypeEnum
{
    entity = "entity",
    entitySet = "entitySet",
}

export type EntityCommandPropertyValues =
    RadObjectPropertyValues
    & {
        description: string;
        entityName: string;
        isDefault: boolean;
        isToggle: boolean;
        key: string;
        owner: any;
        permission: number;
        supportsMultipleRecords: boolean;
        type: EntityCommandTypeEnum;
        visibleEvaluator: IEvaluator;
    };

export type EntityCommandProperties = {
    [name in keyof EntityCommandPropertyValues]: Property<EntityCommandPropertyValues[name]>
};

export interface IEntityCommand extends IRadObject, EntityCommandProperties
{
}

export type EntityCommandConstructor = ControlConstructor & {
    getRequiredFields(): string[];
}

export class EntityCommand<TPropertyValues extends EntityCommandPropertyValues = EntityCommandPropertyValues> extends RadObject<TPropertyValues> implements IEntityCommand
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntityCommand";
    public static override className = "EntityCommand";

    // #region Public Methods

    public static getRequiredFields(): string[]
    {
        return [];
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntityCommandPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                isDefault: false,
                isToggle: false,
                supportsMultipleRecords: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public description!: PropertyString;
    public entityName!: PropertyString;
    public isDefault!: PropertyBoolean;
    public isToggle!: PropertyBoolean;
    public key!: PropertyString;
    public owner!: PropertyObject;
    public permission!: PropertyInt32;
    public supportsMultipleRecords!: PropertyBoolean;
    public type!: Property<EntityCommandTypeEnum>;
    public visibleEvaluator!: Property<IEvaluator>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public clone(): EntityCommand
    {
        const
            visibleEvaluator = this.visibleEvaluator.get(),
            constructor = Object.getPrototypeOf(this).constructor;

        return (
            new constructor({
                description: this.description.get(),
                entityName: this.entityName.get(),
                isDefault: this.isDefault.get(),
                isToggle: this.isToggle.get(),
                key: this.key.get(),
                permission: this.permission.get(),
                type: this.type.get(),
                visibleEvaluator: visibleEvaluator ? visibleEvaluator.clone() : null
            })
        );
    }
    public async executeAsync(entityCommandInformation: EntityCommandInformation, value?: any)
    {
        await this._executeAsync(entityCommandInformation, value);
        entityCommandInformation.triggerHandler("executed");
    }

    // #endregion

    // #region Protected Methods

    protected _descriptionChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected async _executeAsync(entityCommandInformation: EntityCommandInformation, value?: any): Promise<any>
    {
        return Promise.resolve();
    }
    protected _isDefaultChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _isToggleChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _keyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _ownerChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _permissionChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _supportsMultipleRecordsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _typeChanged(change: PropertyChange<EntityCommandTypeEnum>)
    {
        /* Intentionally empty */
    }
    protected _visibleEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.description = new PropertyString(this, propertyValues.description, this._descriptionChanged);
        this.entityName = new PropertyString(this, propertyValues.entityName, this._entityNameChanged);
        this.isDefault = new PropertyBoolean(this, propertyValues.isDefault, this._isDefaultChanged);
        this.isToggle = new PropertyBoolean(this, propertyValues.isToggle, this._isToggleChanged);
        this.key = new PropertyString(this, propertyValues.key, this._keyChanged);
        this.owner = new PropertyObject(this, propertyValues.owner, this._ownerChanged);
        this.permission = new PropertyInt32(this, propertyValues.permission, this._permissionChanged);
        this.supportsMultipleRecords = new PropertyBoolean(this, propertyValues.supportsMultipleRecords, this._supportsMultipleRecordsChanged);
        this.type = new Property<EntityCommandTypeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.type, this._typeChanged);
        this.visibleEvaluator = new Property<IEvaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.visibleEvaluator, this._visibleEvaluatorChanged);
    }

    // #endregion

    // #endregion
}
